<script lang="ts">
	import { beforeUpdate } from 'svelte';
	import { format } from 'svelte-i18n';
	// Stores
	import { firmwares_gen_2 } from 'src/stores/device-runtime/process';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { getDeviceState, getDeviceStateWithDelay } from 'src/stores/device-runtime/states';

	// Services and configs
	import { devices_config } from 'src/configs/devices';
	import { getDeviceChartsConfig } from 'src/helpers/statistics/config/device_chart_config';

	//Helpers
	import {
		is_gen_2,
		isGen2EM,
		real_id,
		isWeatherStation,
		isDistance,
		get_gen_2_component_from_category,
	} from 'src/helpers/interface_helpers';
	import { convert } from 'src/helpers/utilities';
	import { isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';
	import { isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
	import { isVirtualDevice } from 'src/helpers/virtual_helpers/virtual_components_helpers';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	// import { getEmeterDataG2DisplayedFields } from 'src/helpers/emeters_data_helpers';
	import { getAddonStatus } from 'src/helpers/addons/addon_helpers';
	//Design system components
	import { ParameterCardV2 } from '@Shelly/shelly-design-system';

	// Components
	import ControlOnOff from 'src/components/Controls/Single/ControlOnOff.svelte';
	import ControlOnOffAddon from 'src/components/Controls/Single/ControlOnOffAddon.svelte';
	import TimerRemaining from 'src/components/TimerRemaining/TimerRemaining.svelte';
	import PeakPowerInfoV2 from 'src/components/Consumption/PeakPowerInfoV2.svelte';
	import EmeterDataG1 from 'src/components/EmeterData/EmeterDataG1.svelte';
	import EmeterDataG2V2 from 'src/components/EmeterData/EmeterDataG2V2.svelte';
	import EmeterDataG2PhasesV2 from 'src/components/EmeterData/EmeterDataG2PhasesV2.svelte';
	import CustomBoundary from 'src/components/ErrorBoundary/CustomBoundary.svelte';
	import { useNavigateToHistoryStatisticsTab } from 'src/components/Sidepanel/useNavigateToHistoryStatisticsTab';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceLabelList from 'src/components/Sidepanel/DeviceViews/DeviceLabelList.svelte';
	import DeviceViewStateWrapper from '../DeviceViewStateWrapper.svelte';

	// Types
	import type { Device } from 'src/types/Device';

	export let device: Device;

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: deviceConfig = devices_config?.[device?.type];

	// Device calculations
	$: gen2 = is_gen_2(device);

	// Status and label processing
	$: _statuses =
		status?.online && deviceConfig ? (status_configurator(status, device, settings) as any[]) : [];
	$: labelList = _statuses.filter((e) => {
		// Filter out main labes and 'state' label (will be shown as ParameterCard)
		return (
			((e.main === undefined || e.name === 'distance') && isDistance(device.type)) ||
			(e.main === undefined && e.name !== 'state') ||
			isWeatherStation(device.type)
		);
	});

	// Filter out fields that are displayed in parameter cards
	// $: emeterDataG2Fields =
	// 	status?.online && device ? getEmeterDataG2DisplayedFields(device, status) : [];
	// $: filteredLabelList = Array.isArray(labelList)
	// 	? labelList.filter((entry) => !emeterDataG2Fields.includes(entry.name))
	// 	: [];

	let timerLabels: Record<string, any> = {};
	let emeter_timer: boolean | undefined;
	let deviceState: any = getDeviceStateWithDelay(device?.id);

	$: combinedLabelList = [...Object.values(timerLabels), ...labelList];

	$: deviceChartsConfig = device ? getDeviceChartsConfig(device) : [];

	beforeUpdate(() => {
		if (status?.online && deviceConfig) {
			emeter_timer = !gen2 && device?.category === 'emeter' && status?.['relays']?.[0].has_timer;
		}
		deviceState = getDeviceState(device?.id);
	});

	const { navigateToHistoryStatisticsTab } = useNavigateToHistoryStatisticsTab();

	// Total Energy value for display (Gen1: emeters[channel].total, Gen2: em*data:channel.total_act or total_act_energy)
	$: totalEnergyValue = (() => {
		if (!status || device == null) return undefined;
		if (gen2) {
			const emData =
				status?.[`${get_gen_2_component_from_category(device)}data:${device?.channel}`];
			return emData?.total_act ?? emData?.total_act_energy;
		}
		return status?.emeters?.[device?.channel]?.total;
	})();
	$: totalEnergyDisplay =
		totalEnergyValue != null && !Number.isNaN(totalEnergyValue)
			? { value: convert(totalEnergyValue, 'Wh'), hasValue: true }
			: { value: 'N/A', hasValue: false };
</script>

<DeviceViewStateWrapper {device}>
	<div
		slot="staticTop"
		class="label-list-wrapper mb-s d-flex flex-direction-column justify-content-center"
	>
		{#if emeter_timer}
			{#key status['relays'][0].timer_started}
				<TimerRemaining timerKey="gen1_emeter" {device} {status} {settings} bind:timerLabels />
			{/key}
		{/if}
		{#if !isVirtualDevice(device?.type) && !isPeripheralDevice(device?.type) && !isXT1Module(device?.type) && device?.mode !== 'media_player' && !isWeatherStation(device.type)}
			<DeviceLabelList labelList={combinedLabelList} />
		{/if}
		<DeviceNotificationLabel deviceId={device?.id} />
	</div>

	<div class="flex-full-height justify-content-top" slot="content">
		{#if gen2}
			<CustomBoundary
				onError={console.error}
				message={'Problem loading emeter data. Please contact App support.'}
			>
				{#if isGen2EM(device.type) || device.mode === 'monophase'}
					<EmeterDataG2PhasesV2 {device} {status} />
				{:else}
					<EmeterDataG2V2 {device} {status} />
				{/if}
			</CustomBoundary>
		{:else if status?.emeters}
			<EmeterDataG1 {device} {status} />
		{/if}
		<ParameterCardV2
			isFocusable
			title={totalEnergyDisplay.value}
			description={$format('text_total_energy', { default: 'Total Energy' })}
			size="s"
			class="mb-s"
			data-prevent-drawer-swipe
			data-prevent-sidepanel-swipe
			role={deviceChartsConfig.length ? 'button' : undefined}
			on:click={() => deviceChartsConfig.length && navigateToHistoryStatisticsTab()}
		/>
		<PeakPowerInfoV2 {device} />
	</div>

	<div class="flex-full-height justify-content-end" slot="staticBottom">
		{@const peripheral = getAddonStatus(device, [], false, true)}
		{#if !gen2}
			<ControlOnOff {device} {status} {settings} isWide useV2Components={true} />
		{/if}

		{@const emHasSwitch = 'switch:0' in status}
		{@const emHasSwitchAddon = peripheral && peripheral.find((i) => i.peripheralType == 'switch')}

		{#if emHasSwitchAddon && emHasSwitch}
			<div class="d-flex gap-s">
				<div class="control-flex-column flex-1">
					<ControlOnOffAddon
						{device}
						{status}
						isInSlot
						useV2Components={true}
						component={peripheral.find((i) => i.peripheralType == 'switch')?.sensor
							?.component}
					/>
				</div>
				<div class="control-flex-column flex-1">
					<ControlOnOff {device} {status} {settings} isInSlot useV2Components={true} />
				</div>
			</div>
		{:else if emHasSwitch && !emHasSwitchAddon}
			<ControlOnOff {device} {status} {settings} isWide useV2Components={true} />
		{:else if emHasSwitchAddon}
			<ControlOnOffAddon
				{device}
				{status}
				isWide
				useV2Components={true}
				component={peripheral.find((i) => i.peripheralType == 'switch')?.sensor?.component}
			/>
		{/if}
	</div>
</DeviceViewStateWrapper>
